import React, { useEffect, useState } from 'react';
import { useDialog } from '../common/useDialog';
import { usePopPresence, MODAL_CLOSE_MS } from '../common/usePopTransition';
import './ConfirmModal.css';

/**
 * Centered modal with blurred background for confirmations.
 * Use for: At Work change, Delete break, Submit break, role change, etc.
 */
const ConfirmModal = ({
  open,
  title,
  message,
  noteLabel = 'Note (optional)',
  noteRequired = false,
  // A plain yes/no confirmation has nothing to note against it. Defaults to false so every
  // existing caller keeps the textarea it was written for.
  hideNote = false,
  note = '',
  onNoteChange,
  onConfirm,
  onClose,
  confirmLabel = 'Confirm',
  savingLabel = 'Saving...',
  closeLabel = 'Close',
  error = '',
  saving = false,
  // For what cannot be taken back: the confirm button stays disabled until this exact text
  // (case aside) is typed, so a stray click or Enter cannot carry it out.
  confirmPhrase = '',
  confirmPhraseLabel = '',
  // 'danger' draws the confirm button red.
  confirmVariant = 'primary',
  // Anything richer than a sentence (a list of what is about to change), drawn under the message.
  details = null
}) => {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (open) setTyped('');
  }, [open, confirmPhrase]);

  const dialogRef = useDialog(open, () => {
    if (!saving) onClose();
  });

  // Callers reset title, message and note as they close, so the fade-out shows the last
  // content that was on screen rather than an emptied dialog (common/popMotion.css).
  const pop = usePopPresence({ title, message, details, note, error, saving, hideNote, noteLabel, confirmLabel, savingLabel, closeLabel, confirmPhrase, confirmPhraseLabel, confirmVariant }, open, MODAL_CLOSE_MS);
  if (!pop.mounted) return null;
  const shown = pop.value;
  const phraseMissing = !!shown.confirmPhrase
    && typed.trim().toLowerCase() !== String(shown.confirmPhrase).trim().toLowerCase();

  return (
    <div
      ref={dialogRef}
      className={`confirm-modal-overlay ui-modal-backdrop${pop.closing ? ' is-closing' : ''}`}
      onClick={(e) => !saving && e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div className="confirm-modal-content ui-modal" onClick={(e) => e.stopPropagation()}>
        <h3 id="confirm-modal-title" className="confirm-modal-title">{shown.title}</h3>
        <p className="confirm-modal-message">{shown.message}</p>
        {shown.details ? <div className="confirm-modal-details">{shown.details}</div> : null}
        {!shown.hideNote && (
        <div className="confirm-modal-note">
          <label htmlFor="confirm-modal-note-input">{shown.noteLabel}</label>
          <textarea
            id="confirm-modal-note-input"
            value={shown.note}
            onChange={(e) => onNoteChange && onNoteChange(e.target.value)}
            placeholder={noteRequired ? 'e.g. Back from lunch, Left early...' : 'Add a note...'}
            rows={3}
            className="confirm-modal-textarea"
            disabled={shown.saving}
          />
        </div>
        )}
        {shown.confirmPhrase && (
        <div className="confirm-modal-note">
          <label htmlFor="confirm-modal-phrase-input">
            {shown.confirmPhraseLabel || <>Type <strong>{shown.confirmPhrase}</strong> to confirm</>}
          </label>
          <input
            id="confirm-modal-phrase-input"
            type="text"
            className="confirm-modal-phrase"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !phraseMissing && !shown.saving) onConfirm();
            }}
            autoComplete="off"
            spellCheck={false}
            disabled={shown.saving}
            autoFocus
          />
        </div>
        )}
        {shown.error && <p className="confirm-modal-error">{shown.error}</p>}
        <div className="confirm-modal-actions">
          <button
            type="button"
            className={`btn ${shown.confirmVariant === 'danger' ? 'btn-danger' : 'btn-primary'}`}
            onClick={onConfirm}
            disabled={shown.saving || phraseMissing}
            aria-busy={shown.saving}
          >
            {shown.saving ? shown.savingLabel : shown.confirmLabel}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={shown.saving}>
            {shown.closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
